import type {ReactElement} from 'react';
import React from 'react';
import BottomBar from '@Components/contextual-menu/components/bottom-bar';
import {openPosterEditorItemPositionModal} from '@Modals/poster-editor-item-position-modal/poster-editor-item-position-modal';
import {openEditMenuModal} from '@Components/poster-editor/library/poster-editor-open-modals';
import {openPosterEditorItemLayoutModal} from '@Modals/poster-editor-item-layout-modal';
import {openPosterEditorItemFontStyleModal} from '@Modals/poster-editor-item-font-style-modal';
import {openPosterEditorItemFontStyle2Modal} from '@Modals/poster-editor-item-style-2-modal';
import {openPosterEditorItemFontSizeModal} from '@Modals/poster-editor-item-font-size-modal';
import {
  deleteItemByID,
  duplicateActiveItems,
  onCopyStylesClicked,
  showCopyStylesAsNewFeature,
  toggleLockStateForSelectedItem,
} from '@Components/poster-editor/library/poster-editor-library';
import {openPosterEditorTextOutlineModal} from '@Modals/poster-editor-text-outline-modal';
import {openPosterEditorItemOpacityModal} from '@Modals/poster-editor-item-opacity-modal';
import {openPosterEditorHyperlinkModal} from '@Modals/poster-editor-item-hyperlink-modal';
import {openPosterEditorLayoutItemBorderModal} from '@Modals/poster-editor-layout-item-border-modal';
import {openPosterEditorItemBackgroundPanel} from '@Modals/poster-editor-item-background-modal';
import {openPosterEditorLayoutItemSpacingModal} from '@Modals/poster-editor-layout-item-spacing-modal';
import {openPosterEditorMenuItemIconStylesModal} from '@Modals/poster-editor-menu-item-icon-styles-modal';
import {openPosterEditorLayoutFillModal} from '@Modals/poster-editor-layout-fill-modal';
import {openPosterEditorLayoutItemFontModal} from '@Modals/poster-editor-layout-item-font-modal';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {useEditorPrefetchLayoutMobileModules} from '@Hooks/poster-editor/useEditorPrefetchLayoutItemMobileModules';
import {openPosterEditorLineHeightModal} from '@Modals/poster-editor-item-line-height-modal';
import {openPosterEditorItemLetterSpacingModal} from '@Modals/poster-editor-item-letter-spacing-modal';
import {MenuItemBottomBarOptions} from '@Components/poster-editor/components/poster-editor-bottom-bar/poster-editor-bottom-bar.types';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import {useAppSelector} from '@/hooks';
import {getCopiedStyles} from '@Components/poster-editor/poster-editor-reducer';
import {CopyStyleState} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls/poster-item-controls.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {ItemColorFill} from '@Components/poster-editor/components/poster-editor-bottom-bar/components/item-color-fill/item-color-fill';
import {useGetKeyFromActiveItem} from '@Hooks/poster-editor/useGetKeyFromActiveItem';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';
import {PulsatingDot} from '@Components/poster-editor/components/pulsating-dot';
import styles from './menu-bottom-bar.module.scss';
import {openItemAnimationModal} from '@Modals/poster-editor-item-animation-modal';

export function MenuBottomBar(): ReactElement<any> | null {
  const copiedStylesState = useAppSelector(getCopiedStyles) ? CopyStyleState.COPIED : CopyStyleState.COPY;
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const isLocked = useGetKeyFromActiveItem('lockMovementX');
  const isBold = useGetKeyFromTextStylesActiveItem('isBold');
  const isBold2 = useGetKeyFromTableItem('isBold2');
  useEditorPrefetchLayoutMobileModules();

  if (!activeItem?.isMenu()) {
    return null;
  }

  const showDisabledOutlineMessageGrowl = (): void => {
    showMessageGrowl({
      text: window.i18next.t('pmwjs_disabled_text_outline_mobile_text'),
      interval: 2000,
      type: GROWL_TYPE.NEUTRAL,
      showCloseIcon: false,
    });
  };

  const pulsatingDotForCopyStyles = (): ReactElement<any> | null => {
    if (showCopyStylesAsNewFeature()) {
      return <PulsatingDot containerClassname={`_position-absolute  ${styles.pulsatingDotForCopyStyles}`} />;
    }
    return null;
  };

  const onClickHandler = (itemId: MenuItemBottomBarOptions): void => {
    switch (itemId) {
      case MenuItemBottomBarOptions.POSITION:
        openPosterEditorItemPositionModal();
        break;
      case MenuItemBottomBarOptions.CONTENT:
        openEditMenuModal(activeItem);
        break;
      case MenuItemBottomBarOptions.LAYOUT:
        openPosterEditorItemLayoutModal();
        break;
      case MenuItemBottomBarOptions.FONT_1:
        openPosterEditorLayoutItemFontModal({
          panelId: 'poster-editor-layout-item-font-panel',
          fontFamilyOption: FontFamilyOptionType.ONE,
        });
        break;
      case MenuItemBottomBarOptions.STYLE_1:
        openPosterEditorItemFontStyleModal();
        break;
      case MenuItemBottomBarOptions.FONT_2:
        openPosterEditorLayoutItemFontModal({
          panelId: 'poster-editor-layout-item-font-panel',
          fontFamilyOption: FontFamilyOptionType.TWO,
        });
        break;
      case MenuItemBottomBarOptions.STYLE_2:
        openPosterEditorItemFontStyle2Modal();
        break;
      case MenuItemBottomBarOptions.SIZE:
        openPosterEditorItemFontSizeModal();
        break;
      case MenuItemBottomBarOptions.DUPLICATE:
        duplicateActiveItems();
        break;
      case MenuItemBottomBarOptions.FILL:
        openPosterEditorLayoutFillModal();
        break;
      case MenuItemBottomBarOptions.OUTLINE:
        if (!(isBold || isBold2)) {
          showDisabledOutlineMessageGrowl();
          return;
        }
        openPosterEditorTextOutlineModal();
        break;
      case MenuItemBottomBarOptions.SPACING:
        openPosterEditorLayoutItemSpacingModal();
        break;
      case MenuItemBottomBarOptions.LINE_HEIGHT:
        openPosterEditorLineHeightModal();
        break;
      case MenuItemBottomBarOptions.LETTER_SPACING:
        openPosterEditorItemLetterSpacingModal();
        break;
      case MenuItemBottomBarOptions.BACKGROUND:
        openPosterEditorItemBackgroundPanel();
        break;
      case MenuItemBottomBarOptions.BORDER:
        openPosterEditorLayoutItemBorderModal();
        break;
      case MenuItemBottomBarOptions.ICON_STYLES:
        openPosterEditorMenuItemIconStylesModal();
        break;
      case MenuItemBottomBarOptions.COPY_STYLES:
        onCopyStylesClicked();
        break;
      case MenuItemBottomBarOptions.HYPERLINK:
        openPosterEditorHyperlinkModal();
        break;
      case MenuItemBottomBarOptions.OPACITY:
        openPosterEditorItemOpacityModal();
        break;
      case MenuItemBottomBarOptions.ANIMATION:
        openItemAnimationModal();
        break;
      case MenuItemBottomBarOptions.LOCK:
        toggleLockStateForSelectedItem();
        break;
      case MenuItemBottomBarOptions.DELETE:
        deleteItemByID(activeItem.uid);
        break;
      default:
    }
  };

  const getCustomFillContent = (): ReactElement<any> => {
    return <ItemColorFill />;
  };

  return (
    <BottomBar
      id="poster-editing-default-bottom-bar"
      onClick={onClickHandler}
      items={[
        {
          id: MenuItemBottomBarOptions.CONTENT,
          icon: 'icon-pencil',
          text: window.i18next.t('pmwjs_content'),
        },
        {
          id: MenuItemBottomBarOptions.LAYOUT,
          icon: 'icon-layout',
          text: window.i18next.t('pmwjs_layout'),
        },
        {
          id: MenuItemBottomBarOptions.FILL,
          customContent: getCustomFillContent(),
          text: window.i18next.t('pmwjs_fill'),
        },
        {
          id: MenuItemBottomBarOptions.FONT_1,
          icon: 'icon-font-1',
          text: window.i18next.t('pmjws_font_one'),
        },
        {
          id: MenuItemBottomBarOptions.STYLE_1,
          icon: 'icon-text-style-1',
          text: window.i18next.t('pmwjs_style_one'),
        },
        {
          id: MenuItemBottomBarOptions.FONT_2,
          icon: 'icon-font-2',
          text: window.i18next.t('pmjws_font_two'),
        },
        {
          id: MenuItemBottomBarOptions.STYLE_2,
          icon: 'icon-text-style-2',
          text: window.i18next.t('pmwjs_style_two'),
        },
        {
          id: MenuItemBottomBarOptions.SIZE,
          icon: 'icon-table-size',
          text: window.i18next.t('pmwjs_size'),
        },
        {
          id: MenuItemBottomBarOptions.DUPLICATE,
          icon: 'icon-copy',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: MenuItemBottomBarOptions.POSITION,
          icon: 'icon-prints-stack',
          text: window.i18next.t('pmwjs_position'),
        },
        {
          id: MenuItemBottomBarOptions.OUTLINE,
          icon: 'icon-outline',
          text: window.i18next.t('pmwjs_outline'),
          isDisabled: !!(isBold ?? isBold2),
        },
        {
          id: MenuItemBottomBarOptions.SPACING,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_spacing'),
        },
        {
          id: MenuItemBottomBarOptions.LINE_HEIGHT,
          icon: 'icon-line-height',
          text: window.i18next.t('pmwjs_line_height'),
        },
        {
          id: MenuItemBottomBarOptions.LETTER_SPACING,
          icon: 'icon-letter-spacing',
          text: window.i18next.t('pmwjs_letter_spacing'),
        },
        {
          id: MenuItemBottomBarOptions.BACKGROUND,
          icon: 'icon-background',
          text: window.i18next.t('pmwjs_background'),
        },
        {
          id: MenuItemBottomBarOptions.BORDER,
          icon: 'icon-square',
          text: window.i18next.t('pmwjs_border'),
        },
        {
          id: MenuItemBottomBarOptions.ICON_STYLES,
          icon: 'icon-icon-style',
          text: window.i18next.t('pmwjs_icon_styles'),
          textClasses: 'spacing-p-0',
        },
        {
          id: MenuItemBottomBarOptions.COPY_STYLES,
          icon: copiedStylesState === CopyStyleState.COPY ? 'icon-copy-style-brush' : 'icon-paste-style-brush',
          text: copiedStylesState === CopyStyleState.COPY ? window.i18next.t('pmwjs_copy_style') : window.i18next.t('pmwjs_paste_style'),
          isSelected: copiedStylesState === CopyStyleState.COPIED,
          tag: pulsatingDotForCopyStyles(),
        },
        {
          id: MenuItemBottomBarOptions.ANIMATION,
          icon: 'icon-animation',
          text: window.i18next.t('pmwjs_animation'),
        },
        {
          id: MenuItemBottomBarOptions.HYPERLINK,
          icon: 'icon-link',
          text: window.i18next.t('pmwjs_hyperlink'),
        },
        {
          id: MenuItemBottomBarOptions.OPACITY,
          icon: 'icon-opacity',
          text: window.i18next.t('pmwjs_opacity'),
        },
        {
          id: MenuItemBottomBarOptions.LOCK,
          icon: isLocked ? 'icon-lock' : 'icon-unlock',
          text: isLocked ? window.i18next.t('pmwjs_unlock') : window.i18next.t('pmwjs_lock'),
        },
        {
          id: MenuItemBottomBarOptions.DELETE,
          icon: 'icon-delete',
          text: window.i18next.t('pmwjs_delete'),
          className: 'color-danger-100',
        },
      ]}
    />
  );
}
